// Builds the admin preview graph from the server's node definitions.
//
// Every preview is a generic node rendered from the definition itself (see nodes/DynamicNode),
// laid out in a single column down the left of the canvas so the user can scroll through them.

import { COMPACT_NODE_SIZE, isCompactNode } from "./dynamicNodeData";

export const DYNAMIC_NODE_TYPE = "dynamicNode";

/**
 * Lay normalised node definitions out as a React Flow graph.
 *
 * @param {object[]} definitions Normalised nodes (from normalizeWorkflowNodes).
 * @param {object} [options]
 * @param {number} [options.columns] Column count (1 keeps them in a left-hand scrollable list).
 * @param {number} [options.gap] Vertical/horizontal gap between nodes.
 * @param {number} [options.originX] Left margin for the first (only) column.
 * @returns {{nodes: object[], skipped: number}}
 */
export const buildPreviewNodes = (
  definitions = [],
  { columns = 1, gap = 40, originX = 24 } = {}
) => {
  const columnCount = Math.max(1, columns);
  const list = (definitions || []).filter((definition) => definition?.type);

  const maxWidth = list.reduce(
    (max, definition) => Math.max(max, Math.round(definition.layout?.width ?? 280)),
    0
  );

  const columnX = Array.from({ length: columnCount }, (_, index) => originX + index * (maxWidth + gap));
  const columnY = Array.from({ length: columnCount }, () => 24);

  const nodes = [];

  list.forEach((definition) => {
    const compact = isCompactNode(definition);
    const width = compact ? COMPACT_NODE_SIZE : Math.round(definition.layout?.width ?? 280);

    let column = 0;
    for (let index = 1; index < columnCount; index += 1) {
      if (columnY[index] < columnY[column]) column = index;
    }

    // Height is left unset so React Flow measures the node's natural height from its content —
    // the initial size is therefore always appropriate to the data, and it stays resizable.
    nodes.push({
      id: `preview-${definition.type}`,
      type: DYNAMIC_NODE_TYPE,
      position: { x: columnX[column], y: columnY[column] },
      width,
      ...(compact ? { height: COMPACT_NODE_SIZE } : {}),
      data: definition,
      // Interactive: draggable and selectable by default; deletable/connectable stay off.
      deletable: false,
      connectable: false,
    });

    // Advance by an estimate of the rendered height so the column doesn't stack them all at
    // the same spot before measurement completes; the real position is refined by React Flow.
    columnY[column] += (definition.layout?.height ?? 200) + gap;
  });

  return { nodes, skipped: (definitions || []).length - list.length };
};

export default buildPreviewNodes;
